/**
 * WorkspaceSidebar V2 - Sidebar with favorites and collapse.
 *
 * Mobile (< lg): Always collapsed (icon-only, w-14).
 * Desktop (>= lg): User preference via localStorage.
 *
 * @accessibility WCAG 2.2 AA konform
 */

import { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ToolsSidebarContent } from './ToolsSidebarContent';
import { AllToolsModal } from './AllToolsModal';

export interface ToolsSidebarProps {
  className?: string;
}

export function ToolsSidebar({ className }: ToolsSidebarProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [userCollapsed, setUserCollapsed] = useState(() => {
    return localStorage.getItem('workspace-sidebar-collapsed') === 'true';
  });

  // Mobile: always collapsed. Desktop: user preference.
  const collapsed = isDesktop ? userCollapsed : true;

  const handleToggleCollapse = useCallback(() => {
    if (!isDesktop) return; // No toggle on mobile - always collapsed
    setUserCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('workspace-sidebar-collapsed', String(next));
      return next;
    });
  }, [isDesktop]);

  return (
    <>
      <aside
        className={cn(
          'flex flex-col bg-white dark:bg-neutral-900',
          'border-r border-neutral-200 dark:border-neutral-800 flex-shrink-0 shadow-sm',
          'transition-all duration-200',
          collapsed ? 'w-14' : 'w-64',
          className
        )}
      >
        <ToolsSidebarContent
          collapsed={collapsed}
          onToggleCollapse={handleToggleCollapse}
          onManageTools={() => setIsModalOpen(true)}
        />
      </aside>
      <AllToolsModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </>
  );
}

export default ToolsSidebar;
